import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { learningApi } from '../../../api/learning.js';
import { skillsApi } from '../../../api/talent.js';

/**
 * Course catalog + builder entry. Lists course cards and creates a course
 * (optionally with a passing-score assessment and a Skills-Cloud skill grant).
 */
export default function Catalog() {
  const [courses, setCourses] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  function refresh() {
    setLoading(true);
    Promise.all([
      learningApi.listCourses(),
      skillsApi.listSkills().catch(() => ({ items: [] })),
    ])
      .then(([c, s]) => {
        setCourses(c?.items || []);
        setSkills(s?.items || s || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load catalog'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function createCourse(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const passScoreRaw = fd.get('passScore');
    try {
      const { course } = await learningApi.createCourse({
        title: fd.get('title'),
        category: fd.get('category') || null,
        durationMinutes: fd.get('duration') ? Number(fd.get('duration')) : null,
        grantsSkillId: fd.get('grantsSkillId') || null,
        grantsProficiency: fd.get('grantsSkillId') ? 3 : null,
      });
      if (passScoreRaw) {
        await learningApi.upsertAssessment(course.id, { passScore: Number(passScoreRaw), questions: [] });
      }
      e.target.reset();
      setShowCreate(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create course');
    }
  }

  return (
    <section data-testid="learning-catalog-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Course Catalog</h1>
          <p className="muted">{courses.length} courses</p>
        </div>
        <button className="btn primary" data-testid="learning-course-create" onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? 'Close' : '+ Course'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="learning-catalog-error">{error}</div>}

      {showCreate && (
        <form onSubmit={createCourse} className="card form-card grid-form">
          <label>Title<input name="title" data-testid="learning-course-title-input" required /></label>
          <label>Category<input name="category" data-testid="learning-course-category-input" /></label>
          <label>Duration (min)<input name="duration" type="number" min="0" data-testid="learning-course-duration-input" /></label>
          <label>Passing score (%)<input name="passScore" type="number" min="0" max="100" data-testid="learning-course-passscore-input" /></label>
          <label>Grants skill
            <select name="grantsSkillId" defaultValue="" data-testid="learning-course-skill-input">
              <option value="">— none —</option>
              {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="learning-course-submit">Create course</button></div>
        </form>
      )}

      <div className="card">
        <div className="card-grid" data-testid="learning-course-list">
          {courses.map((c) => (
            <div key={c.id} className="card mini" data-testid={`learning-course-card-${c.id}`}>
              <h3>{c.title}</h3>
              <p className="muted">{c.category || '—'} · {c.duration_minutes || 0} min</p>
              {c.grants_skill_name && <span className="badge">Grants: {c.grants_skill_name}</span>}
              {!c.active && <span className="badge warn">inactive</span>}
              <div className="actions">
                <Link className="btn ghost" to={`/learning/catalog/${c.id}`} data-testid={`learning-course-manage-${c.id}`}>Manage</Link>
              </div>
            </div>
          ))}
          {!loading && courses.length === 0 && <p className="muted">No courses yet.</p>}
        </div>
      </div>
    </section>
  );
}
